import {
  ColumnKeyMap,
  LegacySortParams,
  SortDirection,
  SortState,
  SortStorageKeyParts,
  UNSORTED,
} from 'types/sorting';

// --- State transitions ---

export function getNextSortState(current: SortState, clickedKey: string): SortState {
  if (current.columnKey !== clickedKey) {
    return { columnKey: clickedKey, direction: 'asc' };
  }
  if (current.direction === 'asc') return { columnKey: clickedKey, direction: 'desc' };
  if (current.direction === 'desc') return UNSORTED;
  return { columnKey: clickedKey, direction: 'asc' };
}

export function isColumnSorted(state: SortState, columnKey: string): boolean {
  return state.columnKey === columnKey && state.direction !== null;
}

export function getColumnDirection(state: SortState, columnKey: string): SortDirection {
  return state.columnKey === columnKey ? state.direction : null;
}

// --- Request serialization ---

export function toOrdering(state: SortState, columnMap?: ColumnKeyMap): string | null {
  if (!state.columnKey || !state.direction) return null;
  const field = columnMap?.[state.columnKey] ?? state.columnKey;
  return state.direction === 'desc' ? `-${field}` : field;
}

export function toLegacySort(state: SortState, columnMap?: ColumnKeyMap): LegacySortParams {
  if (!state.columnKey || !state.direction) {
    return { sort_by: null, sort_order: null };
  }
  const field = columnMap?.[state.columnKey] ?? state.columnKey;
  return { sort_by: field, sort_order: state.direction };
}

export function fromOrdering(
  ordering: string | null | undefined,
  columnMap?: ColumnKeyMap
): SortState {
  if (!ordering) return UNSORTED;
  const isDesc = ordering.startsWith('-');
  const backendField = isDesc ? ordering.slice(1) : ordering;
  let columnKey = backendField;
  if (columnMap) {
    const entry = Object.entries(columnMap).find(([, v]) => v === backendField);
    if (entry) columnKey = entry[0];
  }
  return { columnKey, direction: isDesc ? 'desc' : 'asc' };
}

export function fromLegacySort(
  sortBy: string | null | undefined,
  sortOrder: 'asc' | 'desc' | null | undefined,
  columnMap?: ColumnKeyMap
): SortState {
  if (!sortBy || !sortOrder) return UNSORTED;
  let columnKey = sortBy;
  if (columnMap) {
    const entry = Object.entries(columnMap).find(([, v]) => v === sortBy);
    if (entry) columnKey = entry[0];
  }
  return { columnKey, direction: sortOrder };
}

// --- Session storage persistence ---

const STORAGE_PREFIX = 'ine-sort';

export function buildSortStorageKey(parts: SortStorageKeyParts): string {
  const segments = [STORAGE_PREFIX, parts.table];
  if (parts.variant) segments.push(parts.variant);
  if (parts.context) segments.push(parts.context);
  return segments.join(':');
}

export function saveSortState(parts: SortStorageKeyParts, state: SortState): void {
  const key = buildSortStorageKey(parts);
  try {
    if (!state.columnKey || !state.direction) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(state));
    }
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

export function loadSortState(parts: SortStorageKeyParts): SortState {
  const key = buildSortStorageKey(parts);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return UNSORTED;
    const parsed = JSON.parse(raw);
    if (parsed?.columnKey && parsed?.direction) {
      return { columnKey: parsed.columnKey, direction: parsed.direction };
    }
    return UNSORTED;
  } catch {
    return UNSORTED;
  }
}

export function clearSortState(parts: SortStorageKeyParts): void {
  const key = buildSortStorageKey(parts);
  try {
    sessionStorage.removeItem(key);
  } catch {
    // fail silently
  }
}
